/**
 * SRTInsightsPanel.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Drop-in insights panel for SchoolReadinessTest (Report tab) and QuickScreener
 * (Result screen). Mirrors the PublicSRTReports analysis quality but written
 * for NISH clinicians rather than parents.
 *
 * Sections (all rendered from a single session's responses):
 *   A. Clinical Summary       — narrative paragraph, overall badge
 *   B. Skill Cluster Analysis — 13 functional clusters with scores, flags,
 *                               clinical text, home activities
 *   C. Strengths / Developing / Priority grids
 *   D. Clinical Recommendations — numbered, colour-coded by urgency
 *   E. Home Activities          — top 3 needs, 2 activities each
 *
 * Props:
 *   responses  — { [itemId]: { level, interacted, note } }  (SRT session)
 *   koshaScores — { annamaya, pranamaya, manomaya, vijnanamaya, anandamaya }
 *   overallScore — number 0–100
 *   childName  — string
 *   childAge   — number | null
 *   disabilities — string[]
 *   KOSHAS     — kosha definition array (passed from parent to avoid duplication)
 *
 * For QuickScreener (no item-level responses):
 *   Pass responses={} and koshaScores from QS domain scores
 *   The cluster analysis gracefully shows "Not assessed" when items are absent
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── Colour tokens ─────────────────────────────────────────────────────────────
const C = {
    navy: "#0B1F35", blue: "#1A4F7A", sky: "#2C7BB6",
    ink: "#182430", muted: "#5C7080", border: "#DDE4EC", fog: "#F2F5F8",
    green: "#1A7A40", greenBg: "#E8F5EE", greenBorder: "#A8D5B5",
    amber: "#B06000", amberBg: "#FEF3E2", amberBorder: "#F0C070",
    red:   "#C0392B", redBg:   "#FDECEA", redBorder:   "#E8A09A",
    teal: "#0E8A7A", tealBg: "#E6F5F3",
  };
  
  // ── Level helpers ─────────────────────────────────────────────────────────────
  const LEVEL_SCORES = { "not-attempted": 0, emerging: 1, developing: 2, achieved: 3 };
  
  function levelFor(pct) {
    if (pct >= 75) return { label: "Achieved",      color: C.green, bg: C.greenBg };
    if (pct >= 45) return { label: "Developing",    color: C.amber, bg: C.amberBg };
    if (pct >   0) return { label: "Emerging",      color: C.red,   bg: C.redBg   };
    return               { label: "Not Assessed",  color: C.muted, bg: C.fog     };
  }
  
  // ── Skill cluster definitions (mirrors PublicSRTReports) ─────────────────────
  const SKILL_CLUSTERS = [
    { id: "gross_motor",      label: "Gross Motor Skills",          icon: "🏃", color: "#0E8A7A", bg: "#E6F5F3",
      desc: "Large muscle movements — balance, coordination, locomotion",
      items: ["an05","an08","an09","an15","an17","an04"] },
    { id: "fine_motor",       label: "Fine Motor Skills",           icon: "✏️", color: "#1A4F7A", bg: "#EBF2FA",
      desc: "Small muscle control — hand-eye coordination, tool use",
      items: ["an01","an02","an06","an07","an10","an11","an16","vj18"] },
    { id: "sensory_body",     label: "Sensory & Body Awareness",    icon: "👁️", color: "#6E3EA6", bg: "#F3EEF9",
      desc: "Spatial awareness, body schema, sensory integration",
      items: ["an03","an04","an12","an14","aa15"] },
    { id: "self_care",        label: "Self-Care & Daily Living",    icon: "🙌", color: "#B45309", bg: "#FEF3C7",
      desc: "Independence in routine tasks and personal care",
      items: ["an13","an18","pr06","pr10","pr15"] },
    { id: "socio_emotional",  label: "Socio-Emotional Development", icon: "💛", color: "#C2620A", bg: "#FEF0E6",
      desc: "Emotional regulation, empathy, social interaction",
      items: ["pr01","pr03","pr04","pr05","pr07","pr08","pr13","pr14","pr17","pr18"] },
    { id: "attention_memory", label: "Attention & Memory",          icon: "🧩", color: "#6E3EA6", bg: "#F3EEF9",
      desc: "Sustained attention, working memory, recall",
      items: ["mn04","mn12","mn16","pr11","mn15"] },
    { id: "cognitive",        label: "Cognitive & Reasoning",       icon: "🧠", color: "#5D4037", bg: "#EFEBE9",
      desc: "Problem solving, classification, logical thinking",
      items: ["mn01","mn02","mn05","mn06","mn07","mn08","mn09","mn10","mn13","mn14","mn17","mn18"] },
    { id: "language",         label: "Language & Communication",    icon: "🗣️", color: "#1A4F7A", bg: "#EBF2FA",
      desc: "Expressive language, comprehension, vocabulary",
      items: ["vj01","vj12","vj16","pr10","mn11","mn18","aa03"] },
    { id: "literacy",         label: "Emergent Literacy",           icon: "📖", color: "#0D5C63", bg: "#E8F8F5",
      desc: "Letter recognition, phonological awareness, reading readiness",
      items: ["vj02","vj05","vj06","vj09","vj11","vj13","vj18"] },
    { id: "numeracy",         label: "Early Numeracy",              icon: "🔢", color: "#B45309", bg: "#FFFBEB",
      desc: "Counting, number sense, quantity comparison",
      items: ["vj03","vj04","vj07","vj08","vj10","vj14","vj17","mn17"] },
    { id: "creative",         label: "Creative Expression",         icon: "🎨", color: "#B03060", bg: "#FCE8F0",
      desc: "Artistic expression, imagination, storytelling",
      items: ["aa02","aa06","aa07","aa12","aa13","aa14","aa17","mn11"] },
    { id: "aesthetic",        label: "Aesthetic & Cultural",        icon: "🏺", color: "#7C3AED", bg: "#EDE9FE",
      desc: "Appreciation of beauty, culture, nature and art",
      items: ["aa04","aa08","aa09","aa10","aa15","aa16","aa17"] },
    { id: "play_imagination", label: "Play & Imagination",          icon: "🎭", color: "#0891B2", bg: "#ECFEFF",
      desc: "Symbolic play, pretend, creative thinking",
      items: ["pr16","aa13","aa14","aa18","mn11","pr12"] },
  ];
  
  // ── Clinical detail per cluster (all three levels + activities) ───────────────
  const CLUSTER_DETAIL = {
    gross_motor: {
      achieved:   "Shows age-appropriate coordination, balance and rhythm. Can participate fully in outdoor play and physical education.",
      developing: "Has foundational movement but shows inconsistency — may jump without a balanced landing or need reminders to stay on a balance line.",
      emerging:   "Large muscle control is still early-stage. May avoid physical challenges or show motor planning delays. Physical or occupational therapy consultation is recommended.",
      activities: [
        { title: "Hopscotch & Jump Squares", desc: "Draw numbered squares with chalk. Have the child hop on one foot, two feet, or jump over gaps. Builds leg strength, balance, and spatial awareness." },
        { title: "Balloon Catch & Throw",    desc: "Toss a balloon back and forth. Balloons move slowly, giving the child time to track and catch. Gradually switch to a soft foam ball." },
        { title: "Copy-the-Clap Game",       desc: "Tap out simple rhythms — 2 claps, pause, 1 clap — and ask the child to copy exactly. Build up to 4-beat patterns." },
      ],
    },
    fine_motor: {
      achieved:   "Demonstrates confident hand control, appropriate pencil grasp, and ability to complete detailed hand tasks independently.",
      developing: "Can attempt most fine motor tasks but shows inconsistency — pencil grip may be immature or colouring may go outside boundaries.",
      emerging:   "Fine motor foundations need significant support. The child may avoid drawing or cutting, or be unable to trace basic shapes. Occupational therapy assessment is strongly recommended.",
      activities: [
        { title: "Tearing & Sticking Collage", desc: "Have the child tear newspaper into small pieces and paste them to make a picture. Develops pincer grip and bilateral hand coordination." },
        { title: "Playdough Shapes & Snakes",  desc: "Roll playdough into snakes, balls, and flat circles. Builds hand strength and pressure control." },
        { title: "Scissor Practice Strip",     desc: "Draw bold straight lines on strips of paper. Cut along the lines with child-safe scissors. Progress from straight to wavy to zigzag." },
      ],
    },
    sensory_body: {
      achieved:   "Has a solid internal map of their body and the space around them. Can follow positional instructions accurately.",
      developing: "Understands basic concepts but may confuse right/left, mix up spatial prepositions, or need multiple prompts before describing a texture.",
      emerging:   "Body awareness and spatial understanding are still forming. Sensory integration activities are recommended.",
      activities: [
        { title: "Body Part Freeze Dance",       desc: "Play music and call out a body part when the music stops — 'Touch your elbow!', 'Stand on your right foot!' Makes body schema fun." },
        { title: "Treasure Hunt with Positions", desc: "Hide a toy and give clues using prepositions: 'It's under the chair', 'Look behind the cushion'." },
        { title: "Texture Feely Box",            desc: "Put objects of different textures in a box. The child touches one and describes it before pulling it out." },
      ],
    },
    self_care: {
      achieved:   "Handles daily routines independently, communicates needs clearly, and has at least one reliable self-regulation strategy.",
      developing: "Can complete most routines with occasional support. May need prompting to ask for help rather than waiting silently.",
      emerging:   "Requires significant adult support for daily routines. May not yet have a calming strategy or express needs in words.",
      activities: [
        { title: "Button & Zip Board",    desc: "Make a dressing frame with 4–5 different fasteners. Practise daily for 5 minutes with no time pressure." },
        { title: "Handwashing Song",      desc: "Use the Happy Birthday song as a timer. Post a picture-card sequence of the 6 steps by the sink." },
        { title: "'I Need…' Visual Board",desc: "Create a card with pictures: water, toilet, help, break, quiet. Teach the child to point and say the word." },
      ],
    },
    socio_emotional: {
      achieved:   "Emotionally aware, socially engaged, and able to regulate feelings and repair relationships. Ready for cooperative learning.",
      developing: "Shows warmth and some emotional vocabulary but may struggle when emotions are intense, or need help identifying fear or surprise.",
      emerging:   "Emotional identification and social engagement are still early-stage. Play therapy or counselling input may be beneficial.",
      activities: [
        { title: "Emotion Mirror Game",      desc: "Sit facing each other with a small mirror. Make a face and ask the child to copy it, then name it." },
        { title: "Feelings Wheel Diary",     desc: "Each evening, point to a feelings wheel and ask the child to pick how they felt today and tell one thing that caused it." },
        { title: "Cooperative Building Game",desc: "Use blocks where each person can only place one block per turn. Requires waiting, sharing, and persistence all at once." },
      ],
    },
    attention_memory: {
      achieved:   "Can hold and use information in mind while doing something else — a strong foundation for academic learning.",
      developing: "Remembers 1–2 items reliably but loses the third under distraction.",
      emerging:   "Working memory and sustained attention need significant support. May recall only 1 item or abandon puzzles quickly.",
      activities: [
        { title: "Shopping List Game",    desc: "Say 'remember to get: apple, ball, and shoe.' Walk to another room to get the items. Start with 2 items, build to 4." },
        { title: "What's Missing? Tray",  desc: "Put 5 objects on a tray. Cover it and secretly remove one. Reveal and ask 'What's missing?'" },
        { title: "Simon Says 3-Step",     desc: "'Simon says: clap your hands, then touch your nose, then sit down.' Give all three instructions before the child moves." },
      ],
    },
    cognitive: {
      achieved:   "Demonstrates flexible, logical thinking. Can classify, sequence, reason about cause and effect, and hold two rules in mind at once.",
      developing: "Handles single-rule tasks well but struggles when two attributes must be considered together.",
      emerging:   "Basic reasoning operations are still forming. May sort randomly or be unable to extend a pattern.",
      activities: [
        { title: "Pattern Stamps & Claps",  desc: "Make a pattern with stamps: red-blue-red-blue. Ask the child to continue it. Pair with clapping." },
        { title: "Story Sequencing Cards",  desc: "Draw 3-picture sequences of familiar events. Cut them up and ask the child to put them in order." },
        { title: "Sorting Treasure Hunt",   desc: "Mix buttons, coins, and pasta. Ask the child to sort by one rule (shape), then re-sort by another (size)." },
      ],
    },
    language: {
      achieved:   "Uses well-formed sentences, can narrate and explain, and engages confidently in verbal communication.",
      developing: "Uses 2–3 word phrases but may give one-word answers to open questions or struggle to retell a story in order.",
      emerging:   "Language is still at an early stage. May use gestures or single words. Speech-language therapy assessment is recommended.",
      activities: [
        { title: "Picture Talk Time",  desc: "Choose a detailed picture book page. Take turns pointing to things and describing them in full sentences." },
        { title: "Bedtime Story Qs",   desc: "After reading, ask: 'What happened first? What did the character feel? What would you do?'" },
        { title: "What If? Jar",       desc: "Write fun 'What if' questions on slips. Pull one at dinner and each person answers in a full sentence." },
      ],
    },
    literacy: {
      achieved:   "Shows strong phonological awareness and early print knowledge. Well-positioned to begin formal reading instruction.",
      developing: "Recognises their name and some letters but phonological awareness tasks (rhyming, blending) are inconsistent.",
      emerging:   "Early literacy skills are just beginning. A structured early literacy programme is recommended.",
      activities: [
        { title: "Rhyme Time Every Day",  desc: "Say a word and ask the child to say a word that rhymes — 'cat… bat… hat…' Use nonsense words too." },
        { title: "Name Letter Hunt",      desc: "Write the child's name on a card. Give them a magazine and ask them to circle every letter from their name." },
        { title: "Sound Robot Game",      desc: "'c-a-t… what's that word?' The child blends it. Then reverse — the child breaks words into robot sounds." },
      ],
    },
    numeracy: {
      achieved:   "Has solid early number foundations. Can count reliably, compare quantities, and order numbers and objects.",
      developing: "Counts well with objects but makes errors beyond 5, or needs a number line to compare written numbers.",
      emerging:   "Number concepts are still forming. May point without one-to-one correspondence or be unable to identify 'more'.",
      activities: [
        { title: "Muffin Tin Counting",   desc: "Put numbers 1–6 in cups of a muffin tin. The child drops the correct number of objects into each cup." },
        { title: "More & Fewer Snack",    desc: "Give two plates with different amounts. Ask 'Which plate has more?' They eat from the bigger plate." },
        { title: "Staircase of Sizes",    desc: "Collect 5 objects of different sizes. Line them up smallest to biggest, then say 'first… second… third…'" },
      ],
    },
    creative: {
      achieved:   "Expresses ideas richly through drawing, storytelling, song, and construction. Has strong imaginative capacity.",
      developing: "Engages willingly with creative tasks but representations may be simple or need a prompt to develop a storyline.",
      emerging:   "Creative expression is at the mark-making stage. A rich arts-based play environment will support this area most.",
      activities: [
        { title: "Story Bag",      desc: "Put 4 unrelated objects in a bag. The child picks them out one by one and must include each in a story." },
        { title: "Mini Art Gallery",desc: "Give any media — crayons, watercolour, torn paper. After finishing, ask 'Tell me about your picture.'" },
        { title: "Karaoke Humming", desc: "Play familiar songs and encourage singing along — even humming counts. Progress to filling in the last word of each line." },
      ],
    },
    aesthetic: {
      achieved:   "Engages deeply with music, nature, art, and cultural traditions. Can reflect on sensory experiences with vocabulary and detail.",
      developing: "Responds to music and shows interest in the environment but descriptions may be brief.",
      emerging:   "Aesthetic and cultural awareness are just beginning. Rich sensory and cultural play experiences are the priority.",
      activities: [
        { title: "Kitchen Colour Mixing",    desc: "Mix food colouring into water: red + blue, yellow + blue. Ask the child to predict the colour first." },
        { title: "Nature Walk & Wonder Log", desc: "On a walk, ask the child to find one beautiful thing, one rough thing, one that smells interesting." },
        { title: "Family Festival Box",      desc: "Create a box of items from one family festival. The child presents: what is the festival, what do we do." },
      ],
    },
    play_imagination: {
      achieved:   "Rich, sustained imaginative play with elaborate scenarios. Approaches new tasks with curiosity rather than anxiety.",
      developing: "Engages in pretend play but scenarios may be brief or rely on familiar scripts.",
      emerging:   "Play is mostly functional rather than symbolic. New activities cause distress. A gentle, low-demand play environment is the priority.",
      activities: [
        { title: "Loose Parts Play Shelf",desc: "Dedicate a shelf to open-ended materials: cardboard tubes, fabric scraps, bottle caps. No instructions." },
        { title: "Puppet Conversation",   desc: "Have two puppets meet and one tells a problem. Ask the child to make the second puppet respond." },
        { title: "Yes-And Storytelling",  desc: "'Once there was a cat who found a key.' Child adds one sentence. You add one. Keep going for 10 turns." },
      ],
    },
  };
  
  // ── Cluster scoring (from item responses) ─────────────────────────────────────
  function clusterScore(cluster, responses) {
    const attempted = cluster.items.filter(id => {
      const r = responses[id];
      return r && (r.interacted === true || (r.level && r.level !== "not-attempted"));
    });
    if (attempted.length === 0) return null;
    const sum = attempted.reduce((s, id) => s + (LEVEL_SCORES[responses[id].level] || 0), 0);
    return Math.round((sum / (attempted.length * 3)) * 100);
  }
  
  // ── Build clinical recommendations from session data ──────────────────────────
  function buildRecommendations(koshaScores, overall, childName, disabilities) {
    const KOSHAS_DEF = [
      { id: "annamaya",    label: "Annamaya (Physical)",           icon: "🏃" },
      { id: "pranamaya",   label: "Pranamaya (Socio-Emotional)",   icon: "💛" },
      { id: "manomaya",    label: "Manomaya (Cognitive)",          icon: "🧠" },
      { id: "vijnanamaya", label: "Vijnanamaya (Language/Literacy)",icon: "📚" },
      { id: "anandamaya",  label: "Anandamaya (Creative)",         icon: "🎨" },
    ];
    const weak    = KOSHAS_DEF.filter(k => (koshaScores[k.id] ?? 0) > 0  && (koshaScores[k.id] ?? 0) < 45);
    const none    = KOSHAS_DEF.filter(k => (koshaScores[k.id] ?? 0) === 0);
    const strong  = KOSHAS_DEF.filter(k => (koshaScores[k.id] ?? 0) >= 75);
  
    const recs = [];
  
    if (overall >= 75) {
      recs.push({ level: "positive", text: `${childName} is demonstrating strong overall school readiness. Continue enrichment activities across all five Panchakosha domains and re-assess in 3 months.` });
    } else if (overall >= 45) {
      recs.push({ level: "moderate", text: `Schedule a structured play-based early intervention programme. Re-assess progress in 6 weeks to evaluate response to intervention.` });
    } else if (overall > 0) {
      recs.push({ level: "urgent",   text: `A comprehensive clinical evaluation at NISH NCAHT is strongly recommended. Priority referral for specialist assessment and IEP development.` });
    }
  
    weak.forEach(k => {
      recs.push({ level: "moderate", text: `${k.icon} Focus targeted intervention on the <strong>${k.label}</strong> domain — currently in the Emerging range. Design domain-specific activities aligned to NCF-FS milestones.` });
    });
  
    if (none.length > 0) {
      recs.push({ level: "info", text: `${none.map(k => k.label).join(", ")} domain(s) were not assessed in this session. A follow-up session to cover these areas is recommended.` });
    }
  
    if (strong.length > 0) {
      recs.push({ level: "positive", text: `${strong.map(k => k.icon + " " + k.label).join(", ")} ${strong.length === 1 ? "is" : "are"} a strength. Leverage these domains as scaffolds when introducing work in weaker areas.` });
    }
  
    if (disabilities?.length > 0) {
      recs.push({ level: "info", text: `Consider disability-specific adaptations for <strong>${disabilities.join(", ")}</strong> — including UDL adjustments, AAC access, and sensory accommodations as appropriate.` });
    }
  
    recs.push({ level: "info", text: `Share this profile with the full support team (special educator, therapist, parent/guardian) and use it to inform the next IEP review cycle.` });
  
    return recs;
  }
  
  // ── Section wrapper ───────────────────────────────────────────────────────────
  function Section({ title, icon, accent = C.sky, children }) {
    return (
      <div style={{ background: "white", borderRadius: 14,
        border: `1.5px solid ${C.border}`,
        boxShadow: "0 1px 6px rgba(0,0,0,.05)",
        overflow: "hidden", marginBottom: 14 }}>
        <div style={{ padding: "11px 16px", borderBottom: `1.5px solid ${C.border}`,
          background: C.fog, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15 }}>{icon}</span>
          <span style={{ fontWeight: 800, fontSize: 13, color: C.ink }}>{title}</span>
          <div style={{ flex: 1, height: 1, background: C.border, marginLeft: 6 }} />
        </div>
        <div style={{ padding: "16px" }}>{children}</div>
      </div>
    );
  }
  
  // ── Single cluster card ───────────────────────────────────────────────────────
  function ClusterCard({ cluster, responses }) {
    const pct       = clusterScore(cluster, responses);
    const attempted = cluster.items.filter(id => {
      const r = responses[id];
      return r && (r.interacted === true || (r.level && r.level !== "not-attempted"));
    }).length;
    const lv        = pct !== null ? levelFor(pct) : { label: "Not Assessed", color: C.muted, bg: C.fog };
    const levelKey  = pct === null ? null : pct >= 75 ? "achieved" : pct >= 45 ? "developing" : "emerging";
    const detail    = CLUSTER_DETAIL[cluster.id];
    const clinText  = detail && levelKey ? detail[levelKey] : null;
  
    return (
      <div style={{ borderRadius: 12, border: `1.5px solid ${C.border}`,
        background: pct === null ? "#FAFAFA" : "white",
        overflow: "hidden", opacity: pct === null ? 0.7 : 1 }}>
        {/* colour bar */}
        <div style={{ height: 4, background: pct !== null ? cluster.color : C.border }} />
  
        <div style={{ padding: "14px 16px" }}>
          {/* Header row */}
          <div style={{ display: "flex", alignItems: "flex-start",
            justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 9, background: cluster.bg,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, flexShrink: 0 }}>{cluster.icon}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{cluster.label}</div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                  {attempted}/{cluster.items.length} items · {cluster.desc}
                </div>
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 10 }}>
              <div style={{ fontSize: 24, fontWeight: 900, lineHeight: 1,
                color: pct !== null ? lv.color : C.muted,
                fontFamily: "Georgia, serif" }}>
                {pct !== null ? `${pct}%` : "—"}
              </div>
              <span style={{ display: "inline-block", background: lv.bg, color: lv.color,
                fontSize: 9, fontWeight: 700, padding: "2px 8px",
                borderRadius: 6, marginTop: 4 }}>{lv.label}</span>
            </div>
          </div>
  
          {/* Progress bar */}
          <div style={{ height: 7, background: "#E0E8F0", borderRadius: 4,
            overflow: "hidden", marginBottom: 10 }}>
            <div style={{ width: `${pct ?? 0}%`, height: "100%",
              background: pct !== null ? cluster.color : C.border,
              borderRadius: 4, transition: "width .5s" }} />
          </div>
  
          {/* Item dots */}
          <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: pct !== null ? 12 : 0 }}>
            {cluster.items.map(id => {
              const r   = responses[id];
              const lev = r?.level || "not-attempted";
              const hit = r?.interacted || (r?.level && r.level !== "not-attempted");
              const dotColor = hit
                ? (lev === "achieved" ? C.green : lev === "developing" ? C.amber : C.red)
                : "#E0E8F0";
              return (
                <div key={id} title={id} style={{ width: 10, height: 10,
                  borderRadius: 2, background: dotColor }} />
              );
            })}
            <span style={{ fontSize: 9, color: C.muted, marginLeft: 4 }}>
              ■ achieved &nbsp;■ developing &nbsp;■ emerging &nbsp;□ not rated
            </span>
          </div>
  
          {/* Clinical text */}
          {clinText && (
            <div style={{ background: lv.bg, borderRadius: 9, padding: "10px 13px",
              marginBottom: 10, border: `1px solid ${lv.color}33` }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: lv.color,
                letterSpacing: .5, marginBottom: 5, textTransform: "uppercase" }}>
                Clinical Observation
              </div>
              <div style={{ fontSize: 12, color: C.ink, lineHeight: 1.75 }}>{clinText}</div>
            </div>
          )}
  
          {/* Home activities */}
          {detail && levelKey && detail.activities && (
            <div style={{ background: "#F0FDF4", borderRadius: 9,
              padding: "10px 13px", border: "1px solid #BBF7D0" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: C.green,
                letterSpacing: .5, marginBottom: 8, textTransform: "uppercase" }}>
                🏠 Home Activities
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {detail.activities.map((act, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ width: 22, height: 22, borderRadius: "50%",
                      background: C.green, color: "white", fontSize: 10,
                      fontWeight: 900, display: "flex", alignItems: "center",
                      justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#166534",
                        marginBottom: 2 }}>{act.title}</div>
                      <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.7 }}>
                        {act.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  // ── Lightweight cluster→kosha mapping for Quick Screener preview mode ─────────
// Majority kosha among each cluster's item-id prefixes. Used only to show an
// indicative outline — never treated as a real per-cluster score.
const CLUSTER_KOSHA = {
  gross_motor: "annamaya", fine_motor: "annamaya", sensory_body: "annamaya",
  self_care: "pranamaya", socio_emotional: "pranamaya",
  attention_memory: "manomaya", cognitive: "manomaya",
  language: "vijnanamaya", literacy: "vijnanamaya", numeracy: "vijnanamaya",
  creative: "anandamaya", aesthetic: "anandamaya", play_imagination: "anandamaya",
};

function QuickClusterPreview({ koshaScores, KOSHAS }) {
  return (
    <div>
      <div style={{ background: C.amberBg, border: `2px solid ${C.amberBorder}`,
        borderRadius: 12, padding: "18px 20px", marginBottom: 16,
        display: "flex", gap: 14, alignItems: "flex-start" }}>
        <span style={{ fontSize: 26, flexShrink: 0 }}>🔒</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#92400E", marginBottom: 5 }}>
            Detailed Skill Cluster Analysis isn't available yet
          </div>
          <div style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.75 }}>
            The 13 functional clusters below need item-level data from the full 90-item Panchakosha SRT
            to score accurately. A Quick Screener only gives a kosha-level signal, so the outline below
            is a rough preview — not a real cluster score.
          </div>
          <div style={{ fontSize: 12.5, color: "#92400E", fontWeight: 700, marginTop: 8 }}>
            Run the full SRT to unlock per-cluster scores, clinical observations, and home activities.
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
        {SKILL_CLUSTERS.map(c => {
          const koshaId = CLUSTER_KOSHA[c.id];
          const kosha   = KOSHAS.find(k => k.id === koshaId);
          const pct     = koshaScores[koshaId] ?? null;
          return (
            <div key={c.id} style={{ border: `1.5px solid ${C.border}`, borderRadius: 10,
              padding: "11px 13px", background: "white" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                <span style={{ fontSize: 16 }}>{c.icon}</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: C.ink }}>{c.label}</span>
              </div>
              {kosha && (
                <span style={{ fontSize: 10, fontWeight: 700, color: kosha.color,
                  background: kosha.bg, padding: "2px 8px", borderRadius: 6 }}>
                  ≈ {kosha.icon} {kosha.label}{pct != null ? ` · ${pct}%` : ""}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
  // ── MAIN EXPORT ───────────────────────────────────────────────────────────────
  export default function SRTInsightsPanel({
    responses    = {},
    koshaScores  = {},
    overallScore = 0,
    childName    = "This child",
    childAge     = null,
    disabilities = [],
    KOSHAS       = [],
    mode = "full",
  }) {
    // Compute all cluster scores upfront
    const allClusters = SKILL_CLUSTERS.map(c => ({
      ...c, pct: clusterScore(c, responses),
    }));
    const assessed  = allClusters.filter(c => c.pct !== null);
    const strengths = assessed.filter(c => c.pct >= 75);
    const developing = assessed.filter(c => c.pct >= 45 && c.pct < 75);
    const priority  = assessed.filter(c => c.pct < 45);
  
    const recs = buildRecommendations(koshaScores, overallScore, childName, disabilities);
    const lv   = levelFor(overallScore);
  
    // Top 3 needs for dedicated home activities section
    const topNeeds = [...assessed].sort((a, b) => a.pct - b.pct).slice(0, 3);
  
    // Rec level colours
    const recStyle = {
      urgent:   { color: C.red,   bg: C.redBg,   border: C.redBorder,   icon: "🚨" },
      moderate: { color: C.amber, bg: C.amberBg, border: C.amberBorder, icon: "📋" },
      positive: { color: C.green, bg: C.greenBg, border: C.greenBorder, icon: "✅" },
      info:     { color: C.sky,   bg: "#EBF4FA", border: "#BDD7EE",     icon: "ℹ️" },
    };
  
    return (
      <div>
  
        {/* ══ A. CLINICAL SUMMARY ════════════════════════════════════════════ */}
        <Section title="Clinical Summary" icon="📊">
          <div style={{ display: "flex", alignItems: "center", gap: 14,
            background: C.navy, borderRadius: 12, padding: "16px 20px", marginBottom: 14 }}>
            <div style={{ textAlign: "center", flexShrink: 0 }}>
              <div style={{ fontSize: 40, fontWeight: 900, color: lv.color,
                fontFamily: "Georgia, serif", lineHeight: 1 }}>{overallScore}%</div>
              <div style={{ background: lv.bg, color: lv.color, fontWeight: 800,
                fontSize: 11, padding: "3px 11px", borderRadius: 8, marginTop: 5,
                display: "inline-block" }}>{lv.label}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.45)",
                fontWeight: 700, marginBottom: 6, letterSpacing: .6 }}>OVERALL READINESS</div>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,.8)",
                lineHeight: 1.8, margin: 0 }}>
                {childName}
                {childAge ? `, age ${childAge},` : ""}
                {disabilities?.length ? ` (${disabilities.join(", ")})` : ""}
                {" "}completed a Panchakosha School Readiness Assessment.{" "}
                {overallScore >= 75
                  ? `Results indicate strong overall readiness across the five developmental domains. Most skills are at the Achieved level.`
                  : overallScore >= 45
                  ? `Results indicate developing readiness. Several skills are progressing well; some areas will benefit from structured support.`
                  : overallScore > 0
                  ? `Results indicate early-stage readiness across multiple domains. A comprehensive clinical evaluation and IEP development are strongly recommended.`
                  : `Assessment data is incomplete — results should be interpreted with caution.`}
                {strengths.length > 0 && ` Cluster strengths observed in: ${strengths.map(c => c.label).join(", ")}.`}
                {priority.length > 0 && ` Priority intervention areas: ${priority.map(c => c.label).join(", ")}.`}
              </p>
            </div>
          </div>
  
          {/* Kosha score bars */}
          {KOSHAS.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {KOSHAS.map(k => {
                const pct = koshaScores[k.id] ?? 0;
                const klv = levelFor(pct);
                const attempted = Object.keys(responses).filter(id =>
                  id.startsWith(k.id.slice(0, 2)) && responses[id]?.interacted
                ).length;
                return (
                  <div key={k.id} style={{ background: C.fog, borderRadius: 9,
                    padding: "11px 14px", border: `1px solid ${C.border}`,
                    borderLeft: `3px solid ${k.color}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ fontSize: 17 }}>{k.icon}</span>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: k.color }}>
                            {k.label || k.short}
                          </div>
                          <div style={{ fontSize: 10, color: C.muted }}>{k.subtitle}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ background: klv.bg, color: klv.color, fontSize: 9,
                          fontWeight: 700, padding: "2px 8px", borderRadius: 7 }}>
                          {klv.label}
                        </span>
                        <span style={{ fontSize: 20, fontWeight: 900, color: klv.color,
                          fontFamily: "Georgia, serif" }}>{pct}%</span>
                      </div>
                    </div>
                    <div style={{ height: 8, background: "#E0E8F0", borderRadius: 4,
                      overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%",
                        background: k.color, borderRadius: 4, transition: "width .5s" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
  
        {/* ══ B. SKILL CLUSTER ANALYSIS ══════════════════════════════════════ */}
        <Section title="Skill Cluster Analysis" icon="🔬">
  {mode === "quick" ? <QuickClusterPreview koshaScores={koshaScores} KOSHAS={KOSHAS} /> : <>
  <div style={{ background: C.fog, border: `1px solid ${C.border}`,
            borderRadius: 10, padding: "11px 15px", marginBottom: 14,
            fontSize: 12, color: C.muted, lineHeight: 1.7 }}>
            <strong style={{ color: C.ink }}>How to read this:</strong> Skills from all five Panchakosha domains
            are grouped into 13 functional clusters. Each cluster score is calculated from the items
            actually attempted in this session — clusters with no rated items show "Not Assessed".
          </div>
  
          {/* Strengths / Developing / Priority summary row */}
          {assessed.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
              gap: 10, marginBottom: 16 }}>
              <div style={{ background: C.greenBg, borderRadius: 10, padding: "12px 14px",
                border: `1.5px solid ${C.greenBorder}` }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.green,
                  marginBottom: 8 }}>✅ STRENGTHS ({strengths.length})</div>
                {strengths.length === 0
                  ? <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>None at Achieved level yet</div>
                  : strengths.map(c => (
                    <div key={c.id} style={{ fontSize: 11, color: "#166534",
                      fontWeight: 600, marginBottom: 4 }}>
                      {c.icon} {c.label} <span style={{ color: C.green }}>({c.pct}%)</span>
                    </div>
                  ))}
              </div>
              <div style={{ background: C.amberBg, borderRadius: 10, padding: "12px 14px",
                border: `1.5px solid ${C.amberBorder}` }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.amber,
                  marginBottom: 8 }}>🔄 DEVELOPING ({developing.length})</div>
                {developing.length === 0
                  ? <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>None in this range</div>
                  : developing.map(c => (
                    <div key={c.id} style={{ fontSize: 11, color: "#92400E",
                      fontWeight: 600, marginBottom: 4 }}>
                      {c.icon} {c.label} <span style={{ color: C.amber }}>({c.pct}%)</span>
                    </div>
                  ))}
              </div>
              <div style={{ background: C.redBg, borderRadius: 10, padding: "12px 14px",
                border: `1.5px solid ${C.redBorder}` }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.red,
                  marginBottom: 8 }}>⚠️ PRIORITY ({priority.length})</div>
                {priority.length === 0
                  ? <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>No clusters at Emerging level</div>
                  : priority.map(c => (
                    <div key={c.id} style={{ fontSize: 11, color: "#991B1B",
                      fontWeight: 600, marginBottom: 4 }}>
                      {c.icon} {c.label} <span style={{ color: C.red }}>({c.pct}%)</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
  
          {/* All 13 cluster cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {SKILL_CLUSTERS.map(cluster => (
              <ClusterCard key={cluster.id} cluster={cluster} responses={responses} />
            ))}
          </div>
  
          {/* Legend */}
          <div style={{ display: "flex", gap: 12, alignItems: "center",
            padding: "10px 14px", background: C.fog, borderRadius: 9,
            border: `1px solid ${C.border}`, marginTop: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.muted }}>Legend:</span>
            {[
              ["#E0E8F0", "Not rated"],
              [C.red,     "Emerging <45%"],
              [C.amber,   "Developing 45–74%"],
              [C.green,   "Achieved 75%+"],
            ].map(([col, label]) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: col }} />
                <span style={{ fontSize: 10, color: C.muted }}>{label}</span>
                </div>
            ))}
          </div>
          </>}
        </Section>
  
        {/* ══ C. CLINICAL RECOMMENDATIONS ════════════════════════════════════ */}
        <Section title="Clinical Recommendations" icon="💡">
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {recs.map((rec, i) => {
              const s = recStyle[rec.level] || recStyle.info;
              return (
                <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start",
                  background: s.bg, borderRadius: 10, padding: "12px 14px",
                  border: `1px solid ${s.border}` }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%",
                    background: s.color, color: "white", fontSize: 12,
                    fontWeight: 900, display: "flex", alignItems: "center",
                    justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: C.ink, lineHeight: 1.75 }}
                      dangerouslySetInnerHTML={{ __html: rec.text }} />
                  </div>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{s.icon}</span>
                </div>
              );
            })}
          </div>
        </Section>
  
        {/* ══ D. HOME ACTIVITIES (top 3 priority clusters) ═══════════════════ */}
        {topNeeds.length > 0 && (
          <Section title="Home Activities for Priority Areas" icon="🏠">
            <div style={{ background: "#EBF4FA", border: "1px solid #BDD7EE",
              borderRadius: 9, padding: "10px 14px", marginBottom: 14,
              fontSize: 12, color: C.sky, lineHeight: 1.7 }}>
              These activities target the clusters where {childName} needs the most support.
              Each takes 10–15 minutes and uses everyday materials. Share with parents/carers for daily home practice.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {topNeeds.map(c => {
                const detail   = CLUSTER_DETAIL[c.id];
                const levelKey = c.pct >= 75 ? "achieved" : c.pct >= 45 ? "developing" : "emerging";
                if (!detail?.activities?.length) return null;
                return (
                  <div key={c.id} style={{ background: "white", borderRadius: 12,
                    padding: "16px 18px", border: `1.5px solid ${C.greenBorder}`,
                    borderLeft: `4px solid ${c.color}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 9,
                        background: c.bg, display: "flex", alignItems: "center",
                        justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                        {c.icon}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: c.color }}>
                          {c.label}
                        </div>
                        <div style={{ fontSize: 10, color: C.muted }}>
                          Current: {c.pct}% · {levelFor(c.pct).label}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {detail.activities.map((act, i) => (
                        <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                          <div style={{ width: 24, height: 24, borderRadius: "50%",
                            background: C.green, color: "white", fontSize: 11,
                            fontWeight: 900, display: "flex", alignItems: "center",
                            justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 800, color: "#166534",
                              marginBottom: 2 }}>{act.title}</div>
                            <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.7 }}>
                              {act.desc}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}
  
        {/* ══ DISCLAIMER ══════════════════════════════════════════════════════ */}
        <div style={{ background: "#FFFBEA", border: "1.5px solid #E8C84A",
          borderRadius: 10, padding: "12px 16px",
          fontSize: 12, color: "#7A6000", lineHeight: 1.75 }}>
          <strong>📌 Clinical Notice:</strong> This Panchakosha profile is a play-based developmental
          observation, not a diagnostic assessment. Results should be interpreted by a qualified special
          educator or developmental specialist. For NISH NCAHT referrals or IEP planning,
          please share this report with the full clinical team.
        </div>
      </div>
    );
  }